import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { listenToCleaningListingAvailability } from "../../../../redux/cleaningReservationSlice";
import { RootState, AppDispatch } from "../../../../redux/store";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import { calculateCleaningEarnings } from "../../../../redux/cleaningFeeSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import { cleaningAmenities } from "../../../../CATEGORIES/Categories";
import countries, { CountryInfo } from "../../../../GLOBAL/Countries";

import {
  Lock, Smartphone, Check, ChevronDown, ChevronUp, ChevronRight,
  Users, Mail, AlertCircle, X, ArrowLeft, ShieldCheck, CreditCard,
} from "lucide-react";

/* ═══════════════════════════════════════════════
   ENDPOINTS
═══════════════════════════════════════════════ */
const STRIPE_CHECKOUT_URL   = "https://europe-west3-alreluxe.cloudfunctions.net/createCleaningCheckoutSession";
const COSMOOLA_CHECKOUT_URL = "https://europe-west3-alreluxe.cloudfunctions.net/createCosmoolaCleaningCheckoutSession";
const CREATE_CUSTOMER_URL   = "https://europe-west3-alreluxe.cloudfunctions.net/createCustomerAccount";

type PaymentMethod = "stripe" | "mpesa";
type CheckoutResponse = { id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string };
type CreateCustomerResponse = { userId: string };

/* ═══════════════════════════════════════════════
   GREEN DESIGN TOKENS
═══════════════════════════════════════════════ */
const G = {
  bg:        "#FFFFFF",
  bgSoft:    "#F0FDF4",
  bgMuted:   "#DCFCE7",
  ink:       "#0D1A0F",
  muted:     "#4B6B52",
  mutedSoft: "#86ABAA",
  border:    "rgba(22,163,74,0.22)",
  borderMid: "rgba(22,163,74,0.45)",
  green:     "#16a34a",
  greenDark: "#14532d",
  greenLight:"#4ade80",
  grad:      "linear-gradient(135deg,#4ade80 0%,#16a34a 40%,#14532d 100%)",
  gradH:     "linear-gradient(90deg,#4ade80 0%,#16a34a 50%,#14532d 100%)",
  red:       "#CC2200",
  mpesa:     "#00A651",
};

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&family=Fraunces:opsz,wght@9..144,400;9..144,700;9..144,900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,input,select,textarea{font-family:inherit;cursor:pointer;}
  ::-webkit-scrollbar{display:none;}

  @keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes slideUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes mpesaPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,166,81,.35)}50%{box-shadow:0 0 0 6px rgba(0,166,81,0)}}
  @keyframes greenPulse{0%,100%{box-shadow:0 0 0 0 rgba(22,163,74,.3)}50%{box-shadow:0 0 0 8px rgba(22,163,74,0)}}

  .fi{width:100%;padding:10px 12px;border:1px solid #DCFCE7;background:#fff;font-family:inherit;font-size:13px;color:#0D1A0F;outline:none;transition:border-color .15s;border-radius:3px;}
  .fi:focus{border-color:#16a34a;}
  .fi::placeholder{color:#86ABAA;}
  .fl{display:block;font-size:10px;font-weight:700;color:#4B6B52;letter-spacing:.12em;text-transform:uppercase;margin-bottom:5px;}

  .pay-method-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
  .pay-method-card{border:2px solid #DCFCE7;background:#fff;border-radius:10px;padding:16px 12px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:7px;transition:all .2s;position:relative;overflow:hidden;user-select:none;}
  .pay-method-card:hover{border-color:#16a34a;background:#F0FDF4;transform:translateY(-2px);}
  .pay-method-card.stripe.selected{border-color:#14532d;background:#F0FDF4;box-shadow:0 6px 20px rgba(20,83,45,.15);transform:translateY(-2px);}
  .pay-method-card.mpesa.selected{border-color:#00A651;background:rgba(0,166,81,.04);box-shadow:0 6px 20px rgba(0,166,81,.2);transform:translateY(-2px);}
  .pay-method-top-bar{position:absolute;top:0;left:0;right:0;height:3px;opacity:0;transition:opacity .18s;}
  .pay-method-card.selected .pay-method-top-bar{opacity:1;}
  .pay-method-check{position:absolute;top:8px;right:8px;width:16px;height:16px;border-radius:50%;border:1.5px solid #DCFCE7;background:#fff;display:flex;align-items:center;justify-content:center;transition:all .15s;}
  .pay-method-card.stripe.selected .pay-method-check{background:#14532d;border-color:#14532d;}
  .pay-method-card.mpesa.selected  .pay-method-check{background:#00A651;border-color:#00A651;}
  .pay-method-label{font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#0D1A0F;text-align:center;}
  .pay-method-sub{font-size:9px;color:#4B6B52;text-align:center;line-height:1.4;}

  .pay-btn-stripe{width:100%;padding:14px 0;border:none;border-radius:5px;font-size:13px;font-weight:700;letter-spacing:.04em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn-stripe.active{background:#14532d;color:#fff;cursor:pointer;box-shadow:0 4px 16px rgba(20,83,45,.3);animation:greenPulse 2.5s ease infinite;}
  .pay-btn-stripe.active:hover{background:#0D3B1F;}
  .pay-btn-stripe.inactive{background:#DCFCE7;color:#86ABAA;cursor:not-allowed;}

  .pay-btn-mpesa{width:100%;padding:14px 0;border:none;border-radius:5px;font-size:13px;font-weight:700;letter-spacing:.04em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn-mpesa.active{background:#00A651;color:#fff;cursor:pointer;box-shadow:0 4px 16px rgba(0,166,81,.25);animation:mpesaPulse 2.5s ease infinite;}
  .pay-btn-mpesa.active:hover{background:#009348;}
  .pay-btn-mpesa.inactive{background:rgba(0,166,81,.10);color:rgba(0,166,81,.4);cursor:not-allowed;}

  .exp-card{border:1px solid #DCFCE7;border-radius:10px;overflow:hidden;margin-bottom:10px;background:#fff;}
  .exp-card-header{display:flex;align-items:center;gap:10px;padding:13px 16px;cursor:pointer;user-select:none;transition:background .13s;}
  .exp-card-header:hover{background:#F0FDF4;}

  .bfs-toggle{display:flex;align-items:center;gap:10px;padding:11px 13px;border:1px dashed rgba(22,163,74,.4);cursor:pointer;transition:all .15s;background:transparent;width:100%;border-radius:4px;text-align:left;}
  .bfs-toggle:hover{border-color:#16a34a;}
  .bfs-toggle.on{border-style:solid;border-color:#16a34a;background:#F0FDF4;}
  .g-check{width:18px;height:18px;border-radius:2px;border:1.5px solid #16a34a;background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .15s;}
  .g-check.on{background:#14532d;border-color:#14532d;}

  .country-dropdown{position:absolute;top:100%;left:0;right:0;z-index:500;background:#fff;border:1px solid #16a34a;border-top:none;max-height:220px;overflow-y:auto;box-shadow:0 8px 24px rgba(0,0,0,.12);}
  .country-option{display:flex;align-items:center;gap:10px;padding:9px 12px;cursor:pointer;font-size:12px;color:#0D1A0F;transition:background .12s;border-bottom:1px solid #F0FDF4;}
  .country-option:hover{background:#F0FDF4;}
  .dial-dropdown{position:absolute;top:100%;left:0;z-index:500;background:#fff;border:1px solid #16a34a;border-top:none;width:220px;max-height:220px;overflow-y:auto;box-shadow:0 8px 24px rgba(0,0,0,.12);}
  .dial-option{display:flex;align-items:center;gap:10px;padding:9px 12px;cursor:pointer;font-size:12px;color:#0D1A0F;transition:background .12s;border-bottom:1px solid #F0FDF4;}
  .dial-option:hover{background:#F0FDF4;}

  .fee-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px dashed rgba(22,163,74,.15);font-size:13px;color:#0D1A0F;}
`;

/* ═══════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════ */
function flagSphere(emoji: string) {
  return (
    <div style={{ position: "relative", width: 22, height: 22, flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "radial-gradient(circle at 35% 32%,rgba(255,255,255,.55) 0%,rgba(255,255,255,.10) 28%,transparent 52%)", zIndex: 1, pointerEvents: "none" }} />
      <div style={{ width: 22, height: 22, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, lineHeight: 1, background: G.bgMuted }}>{emoji}</div>
    </div>
  );
}

function stripDialCode(raw: string, dialCode: string): string {
  const t = raw.trim();
  if (t.startsWith(dialCode)) return t.slice(dialCode.length).trim();
  for (const c of countries) { if (t.startsWith(c.phoneCode)) return t.slice(c.phoneCode.length).trim(); }
  return t.replace(/^\+/, "");
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="fl">{label}</label>{children}</div>
);

/* ═══════════════════════════════════════════════
   COUNTRY / DIAL PICKERS
═══════════════════════════════════════════════ */
const CountryField: React.FC<{ value: string; onChange: (c: CountryInfo) => void }> = React.memo(({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.name === value);
  const filtered = React.useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", border: `1px solid ${open ? G.green : G.border}`, background: "#fff", cursor: "pointer", borderRadius: 3 }}>
        {selected ? <>{flagSphere(selected.emoji)}<span style={{ fontSize: 13, color: G.ink, flex: 1 }}>{selected.name}</span></> : <span style={{ fontSize: 13, color: G.mutedSoft, flex: 1 }}>Select country</span>}
        <ChevronDown size={13} color={G.muted} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="country-dropdown" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid #F0FDF4", position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search country…" value={query} onChange={e => setQuery(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid #DCFCE7", borderRadius: 2, fontFamily: "inherit", fontSize: 12, outline: "none" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="country-option" onClick={() => { onChange(c); setOpen(false); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1 }}>{c.name}</span><span style={{ color: G.muted, fontSize: 11 }}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const DialField: React.FC<{ dialCode: string; onChange: (code: string) => void }> = React.memo(({ dialCode, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.phoneCode === dialCode);
  const filtered = React.useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 10px", border: "1px solid #DCFCE7", borderRight: "none", background: G.bgSoft, cursor: "pointer", minWidth: 90, borderRadius: "3px 0 0 3px" }}>
        {selected && flagSphere(selected.emoji)}
        <span style={{ fontSize: 12, color: G.ink }}>{dialCode}</span>
        <ChevronDown size={11} color={G.muted} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="dial-dropdown" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "7px 8px", borderBottom: "1px solid #F0FDF4", position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} style={{ width: "100%", padding: "6px 8px", border: "1px solid #DCFCE7", borderRadius: 2, fontFamily: "inherit", fontSize: 11, outline: "none" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="dial-option" onClick={() => { onChange(c.phoneCode); setOpen(false); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1, fontSize: 11 }}>{c.name}</span><span style={{ color: G.muted, fontSize: 11, fontWeight: 700 }}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════
   EXPANDABLE CARD
═══════════════════════════════════════════════ */
const ExpandCard: React.FC<{
  title: string; icon?: React.ReactNode; badge?: React.ReactNode;
  defaultOpen?: boolean; forceOpen?: boolean; children: React.ReactNode;
}> = ({ title, icon, badge, defaultOpen = false, forceOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen !== undefined ? forceOpen : open;
  return (
    <div className="exp-card">
      <div className="exp-card-header" onClick={() => forceOpen === undefined && setOpen(v => !v)}>
        {icon && <span style={{ display: "flex", alignItems: "center", color: G.green }}>{icon}</span>}
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" as const, color: G.ink, flex: 1 }}>{title}</span>
        {badge}
        {forceOpen === undefined && (isOpen ? <ChevronUp size={15} color={G.muted} /> : <ChevronDown size={15} color={G.muted} />)}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: .25, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderTop: "1px solid #DCFCE7" }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
export default function CleaningPreview() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch<AppDispatch>();
  const { listingId } = useParams<{ listingId: string }>();

  const { user }           = useSelector((s: RootState) => s.auth);
  const { currentListing } = useSelector((s: RootState) => s.cleaning);
  const listingAvailability = useSelector((s: RootState) => s.cleaningReservations.listingAvailability);
  const { available, conflict } = listingAvailability || { available: true, conflict: null };

  const {
    baseServiceCost, discountedServiceCost, totalServiceCost,
    platformProfit, guestsFee, hostProfit, hostsFee,
    totalCharged, creditsUsed, fullCost,
  } = useSelector((s: RootState) => s.cleaningServiceFees);

  const { convertRaw, symbol } = useCurrency();

  /* ── query params ── */
  const q               = new URLSearchParams(location.search);
  const checkIn         = q.get("checkIn")          || "";
  const checkOut        = q.get("checkOut")         || "";
  const selectedExtras  = JSON.parse(q.get("selectedExtras")  || "[]");
  const reservedDuration= Number(q.get("reservedDuration")    || 0);
  const discountApplied = JSON.parse(q.get("discount")        || "{}");
  const creditsAvailable= Number(q.get("credits")             || 0);
  const totalHours      = Number(q.get("totalHours")          || 0);
  const selectedTimes   = JSON.parse(q.get("selectedTimes")   || "[]");
  const startHour       = q.get("startHour") ? Number(q.get("startHour")) : null;
  const endHour         = q.get("endHour")   ? Number(q.get("endHour"))   : null;
  const rateType        = q.get("rateType")  || currentListing?.rateType  || "perDay";
  const bedroomAmenityId    = q.get("bedroomAmenityId") || "";
  const bedroomAmenityIdNum = bedroomAmenityId ? Number(bedroomAmenityId) : null;
  const bedroomLabel        = bedroomAmenityIdNum
    ? cleaningAmenities.find((a: any) => a.id === bedroomAmenityIdNum)?.name ?? "—"
    : null;

  const isPerHour = rateType === "perHour";
  const isPerDay  = rateType === "perDay";

  /* ── responsive ── */
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.innerWidth >= 900);
  useEffect(() => {
    const r = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  /* ── contact state ── */
  type AddrState = { firstName: string; lastName: string; email: string; country: string; dialCode: string; phone: string };
  const [addr, setAddr] = useState<AddrState>({ firstName: "", lastName: "", email: "", country: "", dialCode: "+254", phone: "" });
  const patchAddr = useCallback((patch: Partial<AddrState>) => setAddr(p => ({ ...p, ...patch })), []);

  /* prefill from auth user */
  useEffect(() => {
    if (!user) return;
    const rawPhone  = user.contactNumber || user.whatsappNumber || "";
    const dialMatch = rawPhone.match(/^(\+\d{1,4})\s?/);
    const dialCode  = dialMatch ? dialMatch[1] : "+254";
    const phone     = dialMatch ? rawPhone.replace(dialMatch[0], "") : rawPhone;
    setAddr({
      firstName: user.firstName || "",
      lastName:  user.lastName  || (user as any).surName || "",
      email:     user.contactEmail || user.email || user.emailAddress || "",
      country:   user.shippingAddress?.country || "",
      dialCode, phone,
    });
  }, [user]);

  const rFirstName = useRef<HTMLInputElement>(null);
  const rLastName  = useRef<HTMLInputElement>(null);
  const rEmail     = useRef<HTMLInputElement>(null);
  const rPhone     = useRef<HTMLInputElement>(null);

  /* ── book for someone else ── */
  const [bookForSomeoneElse,  setBookForSomeoneElse]  = useState(false);
  const [recipientFirstName,  setRecipientFirstName]  = useState("");
  const [recipientLastName,   setRecipientLastName]   = useState("");
  const [recipientEmail,      setRecipientEmail]      = useState("");

  /* ── payment ── */
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("stripe");
  const [payingStripe,    setPayingStripe]    = useState(false);
  const [payingMpesa,     setPayingMpesa]     = useState(false);
  const [payError,        setPayError]        = useState<string | null>(null);
  const paying = payingStripe || payingMpesa;

  /* ── data loading ── */
  useEffect(() => {
    if (!listingId || (currentListing && currentListing.listingId === listingId)) return;
    dispatch(listenToCleaningData(listingId));
  }, [dispatch, listingId, currentListing]);

  useEffect(() => {
    if (!listingId || !checkIn || !checkOut) return;
    const unsub = dispatch(listenToCleaningListingAvailability(listingId, checkIn, checkOut));
    return () => { if (unsub) unsub(); };
  }, [dispatch, listingId, checkIn, checkOut]);

  const allExtras         = currentListing?.extras || {};
  const selectedExtrasMap = selectedExtras.reduce((acc: any, name: string) => {
    if (allExtras[name]) acc[name] = allExtras[name];
    return acc;
  }, {});

  useEffect(() => {
    if (!currentListing?.rate || !currentListing.rateType) return;
    if (!isPerHour && !reservedDuration) return;
    if (isPerHour && totalHours === 0) return;
    const selectedExtrasTotal = Object.values(selectedExtrasMap).reduce((sum: number, v: any) => sum + Number(v), 0);
    const totalDays   = isPerDay                 ? reservedDuration                  : 0;
    const totalWeeks  = rateType === "perWeek"   ? Math.ceil(reservedDuration / 7)   : 0;
    const totalMonths = rateType === "perMonth"  ? Math.ceil(reservedDuration / 30)  : 0;
    const totalYears  = rateType === "perYear"   ? Math.ceil(reservedDuration / 365) : 0;
    const hours       = isPerHour ? totalHours : startHour !== null && endHour !== null ? endHour - startHour : 0;
    dispatch(calculateCleaningEarnings({
      rate: currentListing.rate, rateType: currentListing.rateType as any,
      totalHours: hours, totalDays, totalWeeks, totalMonths, totalYears,
      extraCharges: selectedExtrasTotal, discountApplied, creditsAvailable,
    }));
  }, [dispatch, currentListing, reservedDuration, totalHours, startHour, endHour, discountApplied, creditsAvailable, isPerHour, selectedExtrasMap]);

  /* ── customer account helper ── */
  const ensureCustomerAccount = async (): Promise<string> => {
    if (user && !bookForSomeoneElse) return user.userId;
    const firstName = bookForSomeoneElse ? recipientFirstName : (rFirstName.current?.value || addr.firstName);
    const lastName  = bookForSomeoneElse ? recipientLastName  : (rLastName.current?.value  || addr.lastName);
    const email     = bookForSomeoneElse ? recipientEmail     : (rEmail.current?.value     || addr.email);
    const phone     = `${addr.dialCode}${stripDialCode(rPhone.current?.value || addr.phone, addr.dialCode)}`;
    const res = await axios.post<CreateCustomerResponse>(CREATE_CUSTOMER_URL, {
      firstName, lastName, email, phone,
      bookForSomeoneElse, buyerUserId: user?.userId ?? null,
    });
    if (!res.data?.userId) throw new Error("Account creation failed — no userId returned.");
    return res.data.userId;
  };

  /* ── shared payload builder ── */
  const buildPayload = useCallback(async () => {
    const resolvedUserId = await ensureCustomerAccount();
    return {
      resolvedUserId,
      body: {
        listingId,
        userId:            resolvedUserId,
        buyerUserId:       user?.userId ?? null,
        checkIn, checkOut,
        reservedDuration, totalHours, selectedTimes,
        startHour, endHour,
        selectedExtras,
        bedroomAmenityId:    bedroomAmenityId || null,
        bedroomAmenityLabel: bedroomLabel     || null,
        discountApplied, creditsAvailable,
        rateType,
        bookForSomeoneElse,
        contactFirstName: rFirstName.current?.value || addr.firstName,
        contactLastName:  rLastName.current?.value  || addr.lastName,
        contactEmail:     rEmail.current?.value     || addr.email,
        contactPhone:     `${addr.dialCode}${stripDialCode(rPhone.current?.value || addr.phone, addr.dialCode)}`,
        // pre-computed totals
        baseServiceCost, discountedServiceCost, guestsFee, creditsUsed, totalCharged,
      },
    };
  }, [listingId, user, addr, bookForSomeoneElse, recipientFirstName, recipientLastName, recipientEmail,
      checkIn, checkOut, reservedDuration, totalHours, selectedTimes, startHour, endHour,
      selectedExtras, bedroomAmenityId, bedroomLabel, discountApplied, creditsAvailable, rateType,
      baseServiceCost, discountedServiceCost, guestsFee, creditsUsed, totalCharged]);

  const redirectFromResponse = (data: CheckoutResponse) => {
    const url = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
    if (url)     { window.location.href = url; return; }
    if (data?.id){ window.location.href = `https://checkout.stripe.com/c/pay/${data.id}`; return; }
    throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);
  };

  const handlePayStripe = useCallback(async () => {
    if (paying) return;
    setPayingStripe(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post<CheckoutResponse>(STRIPE_CHECKOUT_URL, body);
      redirectFromResponse(res.data);
    } catch (e: any) {
      setPayError(e?.message ?? "Stripe payment failed. Please try again.");
      setPayingStripe(false);
    }
  }, [paying, buildPayload]);

  const handlePayMpesa = useCallback(async () => {
    if (paying) return;
    setPayingMpesa(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post<CheckoutResponse>(COSMOOLA_CHECKOUT_URL, body);
      const url = res.data?.url ?? res.data?.data?.url ?? res.data?.sessionUrl ?? res.data?.checkoutUrl;
      if (!url) throw new Error(`No Cosmoola URL returned. Server: ${JSON.stringify(res.data)}`);
      window.location.href = url;
    } catch (e: any) {
      setPayError(e?.message ?? "M-Pesa payment failed. Please try again.");
      setPayingMpesa(false);
    }
  }, [paying, buildPayload]);

  /* ── display helpers ── */
  const prettyCheckIn  = dayjs(checkIn).format("DD MMM YYYY");
  const prettyCheckOut = dayjs(checkOut).format("DD MMM YYYY");
  const fmt = (n: number) => convertRaw(n, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const hoursDisplay = isPerHour
    ? selectedTimes.length > 0
      ? `${selectedTimes[0]} → ${selectedTimes[selectedTimes.length - 1]} (${totalHours}h)`
      : `${totalHours}h`
    : startHour !== null && endHour !== null
    ? `${String(startHour).padStart(2,"0")}:00 → ${String(endHour).padStart(2,"0")}:00 (${endHour - startHour}h)`
    : null;

  const durationDisplay = () => {
    if (isPerHour)               return `${totalHours} hour${totalHours > 1 ? "s" : ""}`;
    if (rateType === "perDay")   return `${reservedDuration} day${reservedDuration > 1 ? "s" : ""}`;
    if (rateType === "perWeek")  { const w = Math.ceil(reservedDuration / 7);   return `${w} week${w > 1 ? "s" : ""}`; }
    if (rateType === "perMonth") { const m = Math.ceil(reservedDuration / 30);  return `${m} month${m > 1 ? "s" : ""}`; }
    if (rateType === "perYear")  { const y = Math.ceil(reservedDuration / 365); return `${y} year${y > 1 ? "s" : ""}`; }
    return `${reservedDuration} day${reservedDuration > 1 ? "s" : ""}`;
  };

  const rateSuffix = rateType === "perHour" ? "/ hr" : rateType === "perDay" ? "/ day"
    : rateType === "perWeek" ? "/ week" : rateType === "perMonth" ? "/ month" : "/ year";

  /* ── guards ── */
  if (!currentListing) {
    return (
      <div style={{ background: G.bgSoft, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", gap: 16, flexDirection: "column" }}>
        <CircularProgress size={36} thickness={4} style={{ color: G.green }} />
        <p style={{ fontWeight: 700, color: G.green }}>Loading your booking preview…</p>
      </div>
    );
  }

  if (!available && conflict) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🧹</div>
        <h2 style={{ color: G.greenDark }}>These dates are no longer available</h2>
        <p style={{ color: "#666", margin: "12px 0 24px" }}>Another booking has been confirmed for this period. Please select new dates.</p>
        <button onClick={() => navigate(`/cleaningdetails/${listingId}`)}
          style={{ padding: "12px 24px", background: G.grad, color: "#fff", border: "none", borderRadius: 12, fontWeight: 700, cursor: "pointer" }}>
          Back to calendar
        </button>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     SHARED SECTION COMPONENTS
  ════════════════════════════════════════════ */

  const ListingContent = () => (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      <div onClick={() => navigate(`/cleaningdetails/${listingId}`)}
        style={{ width: 72, height: 72, borderRadius: 12, overflow: "hidden", flexShrink: 0, cursor: "pointer", boxShadow: `0 4px 12px rgba(22,163,74,.25)`, border: `2px solid ${G.border}` }}>
        <img src={currentListing.photos?.[0] || "https://via.placeholder.com/72"} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 15, fontWeight: 900, color: G.ink, lineHeight: 1.25, marginBottom: 4, fontFamily: "'Fraunces', Georgia, serif" }}>
          {currentListing.title || currentListing.name}
        </p>
        <p style={{ fontSize: 12, color: G.muted, lineHeight: 1.4, marginBottom: 6 }}>
          {currentListing.address?.street && `${currentListing.address.street}, `}
          {currentListing.address?.city || currentListing.address?.county}
          {currentListing.address?.country ? `, ${currentListing.address.country}` : ""}
        </p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: G.bgSoft, padding: "3px 8px", borderRadius: 20, border: `1px solid ${G.border}` }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: G.green }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: G.green }}>
            {currentListing.isCompany ? "Professional Company" : "Individual Cleaner"}
          </span>
        </div>
      </div>
    </div>
  );

  const BookingSummaryContent = () => (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid #DCFCE7", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: G.green, marginBottom: 3 }}>SERVICE DATE</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: G.ink }}>{prettyCheckIn || "—"}</div>
        </div>
        <div style={{ padding: "12px 14px", borderLeft: "1px solid #DCFCE7" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: G.green, marginBottom: 3 }}>{isPerHour ? "DURATION" : "END DATE"}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: G.ink }}>{isPerHour ? `${totalHours}h` : prettyCheckOut || "—"}</div>
        </div>
        {bedroomLabel && (
          <div style={{ padding: "10px 14px", borderTop: "1px solid #DCFCE7", gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: G.green, marginBottom: 3 }}>HOME SIZE</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: G.ink }}>{bedroomLabel}</div>
          </div>
        )}
        <div style={{ padding: "10px 14px", borderTop: "1px solid #DCFCE7", gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: G.green, marginBottom: 3 }}>DURATION</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: G.ink }}>{durationDisplay()}</div>
        </div>
      </div>
      {hoursDisplay && (
        <div style={{ padding: "10px 14px", background: G.bgSoft, borderRadius: 8, border: `1px solid ${G.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: G.green, letterSpacing: ".08em", marginBottom: 3 }}>⏱ SERVICE HOURS</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: G.greenDark }}>{hoursDisplay}</div>
          <div style={{ fontSize: 11, color: G.muted, marginTop: 2 }}>{dayjs(checkIn).format("dddd, DD MMM YYYY")}</div>
        </div>
      )}
    </div>
  );

  const ContactContent = () => (
    <div>
      {user && !bookForSomeoneElse && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(22,163,74,.06)", border: "1px solid rgba(22,163,74,.2)", padding: "9px 12px", marginBottom: 14, borderRadius: 4 }}>
          <Check size={12} color={G.green} />
          <p style={{ fontSize: 11, color: G.green }}>Pre-filled from your profile — all fields are still editable.</p>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <Field label="First Name *">
          <input ref={rFirstName} className="fi" placeholder="e.g. Alex" defaultValue={addr.firstName} onBlur={e => patchAddr({ firstName: e.target.value })} />
        </Field>
        <Field label="Last Name *">
          <input ref={rLastName} className="fi" placeholder="e.g. Smith" defaultValue={addr.lastName} onBlur={e => patchAddr({ lastName: e.target.value })} />
        </Field>
      </div>
      <div style={{ marginBottom: 10 }}>
        <Field label="Email Address *">
          <div style={{ position: "relative" }}>
            <input ref={rEmail} className="fi" placeholder="your@email.com" defaultValue={addr.email} onBlur={e => patchAddr({ email: e.target.value })} style={{ paddingLeft: 38 }} />
            <Mail size={13} color={G.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          </div>
        </Field>
      </div>
      <div style={{ marginBottom: 10 }}>
        <Field label="Country *">
          <CountryField value={addr.country} onChange={c => patchAddr({ country: c.name, dialCode: c.phoneCode })} />
        </Field>
      </div>
      <div style={{ marginBottom: 14 }}>
        <Field label="WhatsApp Number *">
          <div style={{ display: "flex" }}>
            <DialField dialCode={addr.dialCode} onChange={dc => patchAddr({ dialCode: dc })} />
            <input ref={rPhone} className="fi" placeholder="e.g. 7001234567" defaultValue={addr.phone}
              onBlur={e => { const clean = stripDialCode(e.target.value, addr.dialCode); patchAddr({ phone: clean }); if (e.target.value !== clean) e.target.value = clean; }}
              style={{ flex: 1, borderRadius: "0 3px 3px 0" }} inputMode="tel" />
          </div>
          <p style={{ fontSize: 10, color: G.muted, marginTop: 4 }}>{addr.dialCode} + your number · dial code added automatically</p>
        </Field>
      </div>

      {/* Book for someone else */}
      <button className={`bfs-toggle${bookForSomeoneElse ? " on" : ""}`} onClick={() => setBookForSomeoneElse(v => !v)}>
        <div className={`g-check${bookForSomeoneElse ? " on" : ""}`}>{bookForSomeoneElse && <Check size={10} color="#fff" />}</div>
        <Users size={13} color={bookForSomeoneElse ? G.ink : G.muted} />
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: bookForSomeoneElse ? G.ink : G.muted, letterSpacing: ".08em" }}>BOOK FOR SOMEONE ELSE</p>
          <p style={{ fontSize: 10, color: G.muted, marginTop: 2 }}>The service will be booked under another person's name</p>
        </div>
      </button>

      <AnimatePresence>
        {bookForSomeoneElse && (
          <motion.div initial={{ opacity: 0, y: -8, height: 0 }} animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }} transition={{ duration: .2 }}
            style={{ marginTop: 10, padding: 14, background: G.bgSoft, border: `1px solid ${G.border}`, borderRadius: 4, overflow: "hidden" }}>
            <p style={{ fontSize: 10, color: G.muted, marginBottom: 12, letterSpacing: ".1em" }}>RECIPIENT DETAILS — an account will be set up for them</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <Field label="First Name *"><input className="fi" placeholder="Their first name" value={recipientFirstName} onChange={e => setRecipientFirstName(e.target.value)} /></Field>
              <Field label="Last Name *"><input className="fi" placeholder="Their last name" value={recipientLastName} onChange={e => setRecipientLastName(e.target.value)} /></Field>
            </div>
            <Field label="Email *">
              <div style={{ position: "relative" }}>
                <input className="fi" placeholder="recipient@email.com" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} style={{ paddingLeft: 38 }} />
                <Mail size={13} color={G.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              </div>
            </Field>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const PriceContent = () => (
    <div>
      <div className="fee-row"><span style={{ color: G.muted }}>Base service cost</span><span style={{ fontWeight: 600 }}>{symbol}{fmt(baseServiceCost)}</span></div>
      {discountedServiceCost < baseServiceCost && (
        <div className="fee-row"><span style={{ color: G.muted }}>Discount</span><span style={{ fontWeight: 600, color: G.green }}>-{symbol}{fmt(baseServiceCost - discountedServiceCost)}</span></div>
      )}
      {Object.entries(selectedExtrasMap).map(([k, v]) => (
        <div key={k} className="fee-row"><span style={{ color: G.muted }}>{k}</span><span style={{ fontWeight: 600 }}>{symbol}{fmt(Number(v))}</span></div>
      ))}
      <div className="fee-row"><span style={{ color: G.muted }}>Service fee</span><span style={{ fontWeight: 600 }}>{symbol}{fmt(guestsFee)}</span></div>
      {creditsUsed > 0 && (
        <div className="fee-row"><span style={{ color: G.muted }}>Credits applied</span><span style={{ fontWeight: 600, color: G.green }}>-{symbol}{fmt(creditsUsed)}</span></div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 12, marginTop: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".15em", color: G.ink }}>TOTAL</span>
        <span style={{ fontSize: 22, fontWeight: 900, background: G.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {symbol}{fmt(totalCharged)}
        </span>
      </div>
      <p style={{ fontSize: 10, color: G.muted, textAlign: "right", marginTop: 2 }}>All taxes included · Charged in EUR</p>
    </div>
  );

  const PaymentContent = () => (
    <div>
      <p style={{ fontSize: 10, fontWeight: 700, color: G.muted, letterSpacing: ".14em", textTransform: "uppercase" as const, marginBottom: 10 }}>Choose payment method</p>
      <div className="pay-method-grid">
        {/* Stripe */}
        <div className={`pay-method-card stripe${selectedPayment === "stripe" ? " selected" : ""}`} onClick={() => setSelectedPayment("stripe")}>
          <div className="pay-method-top-bar" style={{ background: "linear-gradient(90deg,#4ade80,#16a34a,#14532d)" }} />
          <div className="pay-method-check">{selectedPayment === "stripe" && <Check size={9} color="#fff" />}</div>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="28" height="28" rx="4" fill="#635BFF"/>
            <path d="M13.007 11.16c0-.78.642-1.08 1.705-1.08 1.525 0 3.452.46 4.976 1.28V7.6C18.17 6.92 16.66 6.6 15.15 6.6c-3.17 0-5.278 1.66-5.278 4.42 0 4.32 5.948 3.62 5.948 5.48 0 .92-.8 1.22-1.926 1.22-1.665 0-3.79-.68-5.474-1.6v3.8c1.865.8 3.75 1.14 5.474 1.14 3.251 0 5.494-1.6 5.494-4.4-.02-4.66-5.98-3.82-5.98-5.5z" fill="#fff"/>
          </svg>
          <span className="pay-method-label">Stripe</span>
          <span className="pay-method-sub">Cards · Apple Pay · Google Pay</span>
        </div>
        {/* M-Pesa */}
        <div className={`pay-method-card mpesa${selectedPayment === "mpesa" ? " selected" : ""}`} onClick={() => setSelectedPayment("mpesa")}>
          <div className="pay-method-top-bar" style={{ background: "linear-gradient(90deg,#00A651,#007A3D)" }} />
          <div className="pay-method-check">{selectedPayment === "mpesa" && <Check size={9} color="#fff" />}</div>
          <Smartphone size={28} color={selectedPayment === "mpesa" ? G.mpesa : G.muted} />
          <span className="pay-method-label" style={{ color: selectedPayment === "mpesa" ? G.mpesa : undefined }}>M-Pesa</span>
          <span className="pay-method-sub">via Cosmoola · Mobile money</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {selectedPayment === "stripe" ? (
          <motion.button key="stripe-btn" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
            onClick={handlePayStripe} className={`pay-btn-stripe${!paying ? " active" : " inactive"}`}>
            {payingStripe
              ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              : <Lock size={13} />}
            {payingStripe ? "Processing…" : `Pay with Stripe  ${symbol}${fmt(totalCharged)}`}
          </motion.button>
        ) : (
          <motion.button key="mpesa-btn" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
            onClick={handlePayMpesa} className={`pay-btn-mpesa${!paying ? " active" : " inactive"}`}>
            {payingMpesa
              ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              : <Smartphone size={13} />}
            {payingMpesa ? "Connecting to M-Pesa…" : `Pay with M-Pesa  ${symbol}${fmt(totalCharged)}`}
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {payError && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "rgba(204,34,0,.05)", border: "1px solid rgba(204,34,0,.2)", padding: "11px 13px", marginTop: 10, borderRadius: 4 }}>
            <AlertCircle size={13} color={G.red} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 11, color: G.red, flex: 1 }}>{payError}</span>
            <button onClick={() => setPayError(null)} style={{ background: "none", border: "none", padding: 0 }}><X size={12} color={G.red} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <p style={{ fontSize: 11, color: G.muted, textAlign: "center", marginTop: 10 }}>
        You won't be charged until your booking is confirmed
      </p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 18, marginTop: 10 }}>
        {[{ icon: "🔒", l: "Secure" }, { icon: "✅", l: "Verified" }, { icon: "↩️", l: "Refunds" }].map(b => (
          <div key={b.l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 11 }}>{b.icon}</span>
            <span style={{ fontSize: 9, color: G.muted, fontWeight: 700, letterSpacing: ".08em" }}>{b.l.toUpperCase()}</span>
          </div>
        ))}
      </div>
    </div>
  );

  /* ════════════════════════════════════════════
     MOBILE — accordion cards
  ════════════════════════════════════════════ */
  if (!isDesktop) {
    return (
      <div style={{ minHeight: "100vh", background: G.bgSoft, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <style>{CSS}</style>
        {/* sticky header */}
        <div style={{ position: "sticky", top: 0, zIndex: 200, background: "#fff", borderBottom: "1px solid #DCFCE7", boxShadow: "0 2px 10px rgba(22,163,74,.08)" }}>
          <div style={{ height: 3, background: G.grad }} />
          <div style={{ display: "flex", alignItems: "center", padding: "0 16px", height: 50, gap: 14 }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: G.muted, display: "flex", alignItems: "center", gap: 5, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" as const }}>
              <ArrowLeft size={14} /> Back
            </button>
            <p style={{ flex: 1, textAlign: "center", fontFamily: "'Fraunces', Georgia, serif", fontSize: 17, fontWeight: 700, color: G.ink }}>Confirm Booking</p>
            <div style={{ width: 52 }} />
          </div>
        </div>

        <div style={{ padding: "16px 14px 120px" }}>
          <ExpandCard title="Your service" defaultOpen={true}>
            <ListingContent />
            <div style={{ marginTop: 14 }}><BookingSummaryContent /></div>
          </ExpandCard>
          <ExpandCard title="Contact details" icon={<Mail size={13} />} defaultOpen={!!user}>
            <ContactContent />
          </ExpandCard>
          <ExpandCard title="Price breakdown" icon={<CreditCard size={13} />} defaultOpen={false}>
            <PriceContent />
          </ExpandCard>
          <ExpandCard title="Payment" icon={<ShieldCheck size={13} />} defaultOpen={true}>
            <PaymentContent />
          </ExpandCard>
        </div>

        {/* mobile sticky bar */}
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,.97)", borderTop: "1px solid #DCFCE7", padding: "12px 16px", zIndex: 200, backdropFilter: "blur(10px)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: G.muted }}>Total</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: G.ink }}>{symbol}{fmt(totalCharged)}</span>
          </div>
          <AnimatePresence mode="wait">
            {selectedPayment === "stripe" ? (
              <motion.button key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .15 }}
                onClick={handlePayStripe} className={`pay-btn-stripe${!paying ? " active" : " inactive"}`}>
                {payingStripe ? <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} /> : <Lock size={12} />}
                {payingStripe ? "Processing…" : `Stripe  ${symbol}${fmt(totalCharged)}`}
              </motion.button>
            ) : (
              <motion.button key="m" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .15 }}
                onClick={handlePayMpesa} className={`pay-btn-mpesa${!paying ? " active" : " inactive"}`}>
                {payingMpesa ? <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} /> : <Smartphone size={12} />}
                {payingMpesa ? "Connecting…" : `M-Pesa  ${symbol}${fmt(totalCharged)}`}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     DESKTOP — two-column full cards
  ════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: "#ECFDF5", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{CSS}</style>

      {/* header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #DCFCE7", boxShadow: "0 1px 8px rgba(22,163,74,.06)" }}>
        <div style={{ height: 3, background: G.grad }} />
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", padding: "0 40px", height: 56 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: G.muted, display: "flex", alignItems: "center", gap: 6, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" as const }}>
            <ArrowLeft size={14} /> Back
          </button>
          <p style={{ flex: 1, textAlign: "center", fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 700, color: G.ink, letterSpacing: ".02em" }}>
            Confirm your booking
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {[{ icon: <ShieldCheck size={13} color={G.green} />, l: "Encrypted" }, { icon: <CreditCard size={13} color={G.green} />, l: "Stripe" }].map(b => (
              <div key={b.l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {b.icon}<span style={{ fontSize: 10, color: G.muted, fontWeight: 700 }}>{b.l.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 40px 60px", display: "grid", gridTemplateColumns: "minmax(0,1fr) 360px", gap: 28, alignItems: "start" }}>

        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#fff", border: "1px solid #DCFCE7", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 14px rgba(22,163,74,.07)", animation: "slideUp .3s ease" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #DCFCE7" }}>
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: G.green, marginBottom: 12 }}>YOUR SERVICE</p>
              <ListingContent />
            </div>
            <div style={{ padding: "16px 20px" }}><BookingSummaryContent /></div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #DCFCE7", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 14px rgba(22,163,74,.07)", animation: "slideUp .35s ease" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #DCFCE7", display: "flex", alignItems: "center", gap: 10 }}>
              <Mail size={14} color={G.green} />
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: G.ink }}>CONTACT DETAILS</p>
              {user && <span style={{ marginLeft: "auto", fontSize: 10, color: G.green, fontWeight: 700, background: "rgba(22,163,74,.07)", padding: "2px 8px", borderRadius: 3 }}>● Pre-filled</span>}
            </div>
            <div style={{ padding: "16px 20px" }}><ContactContent /></div>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ position: "sticky", top: 88, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#fff", border: "1px solid #DCFCE7", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 14px rgba(22,163,74,.07)", animation: "slideUp .28s ease" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #DCFCE7", display: "flex", alignItems: "center", gap: 10 }}>
              <CreditCard size={14} color={G.green} />
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: G.ink }}>PRICE BREAKDOWN</p>
            </div>
            <div style={{ padding: "14px 20px" }}><PriceContent /></div>
          </div>
          <div style={{ background: "#fff", border: "1px solid #DCFCE7", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 14px rgba(22,163,74,.07)", animation: "slideUp .32s ease" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #DCFCE7", display: "flex", alignItems: "center", gap: 10 }}>
              <ShieldCheck size={14} color={G.green} />
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: G.ink }}>PAYMENT</p>
            </div>
            <div style={{ padding: "16px 20px" }}><PaymentContent /></div>
          </div>
        </div>
      </div>
    </div>
  );
}